import { createFileRoute, Link } from "@tanstack/react-router";
import { useProfile, useInvestments, useTransactions, useAnnouncements } from "@/lib/data-hooks";
import { StatCard } from "@/components/app/stat-card";
import { Wallet, TrendingUp, ArrowDownToLine, ArrowUpFromLine, Megaphone, Gift, Lock, CheckCircle2 } from "lucide-react";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PortfolioPanel } from "@/components/app/portfolio-panel";
import { PortfolioInsights } from "@/components/app/portfolio-insights";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard — CryptoBull Pro" }] }),
  component: Dashboard,
});

function Dashboard() {
  const { data: profile } = useProfile();
  const { data: investments = [] } = useInvestments();
  const { data: txs = [] } = useTransactions(8);
  const { data: announcements = [] } = useAnnouncements();

  const activeInv = investments.filter((i) => i.status === "active");
  const isBonus = Boolean((profile as any)?.is_bonus_user);
  const bonusAmount = Number((profile as any)?.bonus_amount ?? 0);
  const bonusRequired = Number((profile as any)?.bonus_required_investment ?? 0);
  const totalInvested = investments.reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const progressPct = bonusRequired > 0 ? Math.min(100, (totalInvested / bonusRequired) * 100) : 0;
  const unlocked = bonusRequired > 0 && totalInvested >= bonusRequired;
  const remaining = Math.max(0, bonusRequired - totalInvested);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Welcome back, {profile?.full_name ?? "trader"}</h1>
        <p className="text-sm text-muted-foreground">Here's your portfolio at a glance.</p>
      </div>

      {isBonus && (
        <div className="glass rounded-2xl p-5 border border-primary/30">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-primary/15 p-2.5">
                {unlocked ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <Gift className="h-5 w-5 text-primary" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold">Welcome bonus: {fmtMoney(bonusAmount)}</h2>
                  <Badge variant={unlocked ? "default" : "outline"} className="text-[10px]">
                    {unlocked ? "Withdrawals unlocked" : "Locked"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  {unlocked
                    ? `You've invested ${fmtMoney(totalInvested)} — you can now withdraw your bonus, investments, and all profits.`
                    : `Invest a total of ${fmtMoney(bonusRequired)} to unlock withdrawals. Once unlocked, you can pull out the bonus plus your investments and earnings.`}
                </p>
              </div>
            </div>
            {!unlocked && (
              <Button asChild size="sm"><Link to="/plans">Invest now</Link></Button>
            )}
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
              <span className="flex items-center gap-1">
                {!unlocked && <Lock className="h-3 w-3" />}
                Invested {fmtMoney(totalInvested)} of {fmtMoney(bonusRequired)}
              </span>
              <span>{progressPct.toFixed(0)}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${unlocked ? "bg-emerald-500" : "bg-primary"}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {!unlocked && remaining > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2">
                {fmtMoney(remaining)} more in investments needed to unlock withdrawals.
              </p>
            )}
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] rounded-lg bg-white/5 border border-white/10 p-2.5">
              <div>
                <div className="text-muted-foreground">Threshold</div>
                <div className="font-mono mt-0.5">{fmtMoney(bonusRequired)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Total invested</div>
                <div className="font-mono mt-0.5">{fmtMoney(totalInvested)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Progress</div>
                <div className="font-mono mt-0.5">
                  {fmtMoney(totalInvested)} / {fmtMoney(bonusRequired)} = {progressPct.toFixed(2)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Balance" value={fmtMoney(profile?.balance)} icon={Wallet} accent="gold" />
        <StatCard label="Total Earned" value={fmtMoney(profile?.total_earned)} icon={TrendingUp} accent="success" />
        <StatCard label="Total Deposited" value={fmtMoney(profile?.total_deposited)} icon={ArrowDownToLine} accent="primary" />
        <StatCard label="Total Withdrawn" value={fmtMoney(profile?.total_withdrawn)} icon={ArrowUpFromLine} accent="danger" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <PortfolioPanel balance={Number(profile?.balance ?? 0)} />
        </div>

        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">Active Investments</div>
            <Button asChild size="sm" variant="ghost"><Link to="/deposit">+ New</Link></Button>
          </div>
          {activeInv.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No active investments. <Link to="/plans" className="text-primary">Browse plans →</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {activeInv.slice(0, 5).map((i) => (
                <div key={i.id} className="rounded-lg bg-white/5 p-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">{i.plan?.name}</span>
                    <span className="font-mono text-sm">{fmtMoney(i.amount)}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex justify-between">
                    <span>Earned {fmtMoney(i.earned)}</span>
                    <span>Ends {fmtDate(i.end_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PortfolioInsights />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">Recent Activity</div>
            <Button asChild size="sm" variant="ghost"><Link to="/transactions">View all →</Link></Button>
          </div>
          {txs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No transactions yet.</div>
          ) : (
            <div className="space-y-2">
              {txs.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-white/5">
                  <div className="flex items-center gap-3">
                    <span className="capitalize text-sm">{t.type}</span>
                    <Badge variant="outline" className="text-xs capitalize">{t.status}</Badge>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">{fmtMoney(t.amount)}</div>
                    <div className="text-[11px] text-muted-foreground">{fmtDate(t.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><Megaphone className="h-4 w-4 text-primary" /><span className="text-sm text-muted-foreground">Announcements</span></div>
          {announcements.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No announcements</div>
          ) : (
            <ul className="space-y-3">
              {announcements.map((a) => (
                <li key={a.id}>
                  <div className="text-sm font-medium">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.body}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}